import {
    Container, Image, Modal, Nav, Navbar, Offcanvas,
} from 'react-bootstrap';
import { BsJustify } from "react-icons/bs";
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import AddShoppingCart from '@mui/icons-material/AddShoppingCart';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Link, useNavigate, useLocation} from 'react-router-dom';
import { BrowserView, MobileView } from 'react-device-detect';
import { FaUserCircle } from "react-icons/fa";
import Button from "react-bootstrap/Button";

import { RootState } from 'app/store';
import Cart from 'app/features/Order/Cart/Cart';
import UpsellModal from 'app/features/Order/Upsell/UpsellModal';
import getBrandName from 'app/shared/utils/BrandHelper';
import {
    toggleAuthModal,
    toggleCartDrawer,
    toggleUpsellModal,
    toggleMoreOptionsDrawer,
    setResetPasswordSuccessMsg,
    setForceLogoutErrorMsg,
    setCredentials,
} from "app/store/slices/authSlice";
import { updateShowChangePickUpDetailsModal } from "app/store/slices/storeSlice";
import PickUpSelectionsFromAnywhere from 'app/features/PickUpSelections/PickUpSelectionsFromAnywhere';
import LoginView from 'app/features/Login/LoginView';
import WelcomeModal from 'app/features/MyAccount/WelcomeModal';
import EnrollModal from 'app/features/MyAccount/EnrollModal';
import { redirectToBrandSite } from "app/shared/utils/utilityFunctions";
import ARIA_LABELS from 'constants/ariaLabels';
import ShellCartFloatingButton from 'components/ShellCartFloatingButton';
import useGetPlatformInfo from 'hooks/useGetPlatformInfo';
import { isRewardExpired } from 'app/features/RewardsHome/RewardsUtility';
import { FUTURE_ORDERS_FEATURE_FLAG, Routes } from 'app/shared/constants/constants';
import { useAppSelector } from 'store/hooks';
import {
    useCreateOrderMutation,
    useUpdateOrderMutation,
} from 'store/services/tOrderApi';
import { OrderPlacementResponse } from 'store/slices/tOrderTypes';
import { getOrderPayload, getSelectedRewards, getSelectedRewardValidity } from 'store/slices/orderSliceUtil';
import { useGuestMutation } from 'app/services/api/mutateGateAPI';
import {v4 as uuidv4} from 'uuid';
import { GuestRequest, GuestResponse, GuestResponseType } from 'app/features/Guest/types/Guest';
import T_ROUTES from 'routes/routePaths';
import TCart from 'features/order/cart';
import { setHasRewaredSelected, updateSelectedReward } from 'app/store/slices/rewardsSlice';
import { RewardsDetails } from 'app/features/Rewards/types/RewardsDetails';
import { updateRewardSelectedOnPage } from 'store/slices/tOrderSlice';
import useIsTStore from 'hooks/useIsTStore';
import UserProfileView from "./UserProfileView";
import MoreOptions from "./MoreOptions";
import useAppUtil from "./useAppUtil";
import AutoHideSnackbar from './AutoHideSnackbar';
import { ReactComponent as RewardsIcon } from "../../../assets/images/ebb_rewards.svg";
import { ReactComponent as MenuIcon } from "../../../assets/images/ebb_menu.svg";
import { ReactComponent as LocationsIcon } from "../../../assets/images/ebb_locations.svg";
import { ReactComponent as NutritionIcon } from "../../../assets/images/ebb_nutrition.svg";
import { ReactComponent as RecentOrdersIcon } from "../../../assets/images/ebb_recent_orders.svg";
import PickupModal from './PickupModal';
import useFeatureFlags from '../hooks/useFeatureFlags';
import ModalLoader from './ModalLoader';
import CartDrawer from './CartDrawer';
import { decrypt, deleteKeyPersist } from '../utils/EncryptionUtility';
import LABELS from '../constants/Literals';

const uniqueId = uuidv4();
const { brandLogoPath, brandTitle, giftCardIcon } = getBrandName();

const NavbarComp: React.FC = () => {
    const { logoutHandler } = useAppUtil();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [createGuestToken, {isLoading: isGuestTokenCreating}] = useGuestMutation();
    const { pathname } = useLocation();
    const {
        hideNavigation,
        modalOpen,
        cartOpen,
        hasSeenUpsellModal,
        upsellModalOpen,
        showMoreOptions,
    } = useSelector((state: RootState) => state.auth);
    const {
        hasRewaredSelected,
        selectedReward,
    } = useSelector((state: RootState) => state.rewards);
    const {
        totalItems,
        itemsInCart,
        navBarButtonDisable,
    } = useSelector((state: RootState) => state.orders);
    const {
        totalCartItems,
        hasRewardSelectedFromPage,
    } = useAppSelector((state) => state.tOrders);
    const isSelectedRewardValid = useAppSelector(getSelectedRewardValidity);
    const availableReward = useAppSelector(getSelectedRewards);
    const [
        createOrderMutation,
        {
            isLoading: isOrderCreationInprogress,
        },
    ] = useCreateOrderMutation();
    const [
        updateOrderMutation,
        {
            isLoading: isOrderUpdateInProgress,
        },
    ] = useUpdateOrderMutation();
    const [orderErrorCode, updateOrderErrorCode] = useState<string | null>(null);
    const isAnyApiLoading = isOrderCreationInprogress
    || isOrderUpdateInProgress
    || isGuestTokenCreating;
    const {
        orderId,
        ...orderRequestPayload
    } = useSelector(getOrderPayload);
    function updatePostOrderState(orderResponse: OrderPlacementResponse["data"]) {
        const { orderTransactionId } = orderResponse;
        dispatch(toggleCartDrawer(false));
        navigate(`${T_ROUTES.CONFIRMATION}/${orderTransactionId}`);
    }

    const { user, isGuest, configs } = useSelector((state: RootState) => state.auth);

    // Check if its a Toast store
    const isTStore = useIsTStore();
    const totalItemsInTheCart = (isTStore ? totalCartItems : totalItems) || 0;
    const upsellFeatureEnabled = configs.env.REACT_APP_UPSELL_ENABLED === 'true';
    const hasCartItemsForUpsell = isTStore
        ? totalCartItems > 0
        : itemsInCart.length > 0;
    const shouldOpenUpsell = !hasSeenUpsellModal
        && upsellFeatureEnabled
        && hasCartItemsForUpsell;
    const [isFutureOrdersEnabled] = useFeatureFlags(FUTURE_ORDERS_FEATURE_FLAG);
    const { isShellApp } = useGetPlatformInfo();
    const shouldShowShellCart = !hideNavigation
        && (pathname === T_ROUTES.CATEGORY || pathname === T_ROUTES.MENU);

    const redirectToHome = () => {
        // redirect to home on clicking sign in from navigation header
        // for the signup, forgot password and reset password deeplinks
        const pages = ["signup", "forgotpassword", "signUp", "forgotPassword", "passwordreset", "passwordReset"];
        if (pages.some((data) => window.location.href.includes(data))) {
            navigate("/");
        }
    };
    const handleModal = () => {
        dispatch(toggleAuthModal(!modalOpen));
        dispatch(setResetPasswordSuccessMsg(""));
        dispatch(setForceLogoutErrorMsg(null));
        redirectToHome();
    };
    const handleLogout = () => {
        dispatch(toggleAuthModal(false));
        logoutHandler();
    };
    const hideModal = () => {
        dispatch(toggleAuthModal(false));
        // setModalOpen(false);
    };
    const closeCart = () => {
        dispatch(toggleCartDrawer(false));
        updateOrderErrorCode(null);
    };
    const closeMoreOptions = () => {
        dispatch(toggleMoreOptionsDrawer(false));
    };
    const openCart = () => {
        if (shouldOpenUpsell) {
            //show upsell modal
            dispatch(toggleUpsellModal(true));
            return;
        }
        // Dispatch cart top bar event.
        dispatch(toggleCartDrawer(true));
    };
    const closeUpsellModal = (shouldOpenCart: boolean) => {
        dispatch(toggleUpsellModal(false));
        if (shouldOpenCart) dispatch(toggleCartDrawer(true));
    };
    const openMobileSignup = () => {
        if (isGuest || !user) {
            dispatch(toggleAuthModal(true));
        }
        dispatch(setResetPasswordSuccessMsg(""));
        redirectToHome();
    };
    const redirectToMenu = () => {
        navigate("/category");
    };

    const {
        showChangePickUpDetailsModal,
        storeNumber,
        pickUpDate,
        pickUpTime,
        pickUpType,
    } = useSelector((state: RootState) => state.stores);

    const hidePickupModal = () => {
        dispatch(updateShowChangePickUpDetailsModal(false));
    };

    const handlePickupInfoChange = (selectedPickupDate: string) => {
        if (pathname === Routes.HOME) {
            // if the user is on the home page and reward is not available for the selected date,
            // redirect to the rewards page
            if (hasRewaredSelected && isRewardExpired(selectedReward, selectedPickupDate)) {
                navigate(Routes.REWARDS);
                return;
            }
            navigate(Routes.CATEGORY);
        }
        // if the user is on the rewards page and reward is available for the selected date,
        // redirect to the category page
        if (
            pathname === Routes.REWARDS
            && hasRewaredSelected
            && !isRewardExpired(selectedReward, selectedPickupDate)
        ) {
                navigate(Routes.CATEGORY);
        }
    };
    const [navbarExpanded, setNavbarExpanded] = useState(false);
    const navItems = () => (
        <>
            <Nav.Item>
                <Nav.Link
                    disabled={navBarButtonDisable}
                    eventKey="1"
                    className="d-flex flex-row navBarBrandLink justify-content-start"
                    onClick={() => {
                        redirectToMenu();
                    }}
                >
                    <MenuIcon className="brand-icons" />
                    <p className="menuFont">MENU</p>
                </Nav.Link>
            </Nav.Item>
            <Nav.Item>
                <Nav.Link
                    disabled={navBarButtonDisable}
                    eventKey="2"
                    className="d-flex flex-row navBarBrandLink verticalLine justify-content-start"
                    as={Link}
                    to="/"
                >
                    <LocationsIcon className="brand-icons" />
                    <p className="menuFont">LOCATION</p>
                </Nav.Link>
            </Nav.Item>
            <Nav.Item>
                <Nav.Link
                    disabled={navBarButtonDisable}
                    eventKey="3"
                    className="d-flex flex-row navBarBrandLink verticalLine justify-content-start"
                    href={configs?.brandConfigs?.NUTRITION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <NutritionIcon className="brand-icons" />
                    <p className="menuFont">NUTRITION</p>
                </Nav.Link>
            </Nav.Item>
            {user && !isGuest && (
                <Nav.Item>
                    <Nav.Link
                        disabled={navBarButtonDisable}
                        eventKey="4"
                        className="d-flex flex-row navBarBrandLink verticalLine justify-content-start"
                        as={Link}
                        to="/recentorders"
                    >
                        <RecentOrdersIcon className="brand-icons" />
                        <p className="menuFont" style={{ whiteSpace: "nowrap" }}>{LABELS.RECENT_ORDERS.orders}</p>
                    </Nav.Link>
                </Nav.Item>
            )}
            {user && !isGuest && (
                <Nav.Item>
                    <Nav.Link
                        disabled={navBarButtonDisable}
                        eventKey="5"
                        className="d-flex flex-row navBarBrandLink verticalLine justify-content-start"
                        as={Link}
                        to="/rewards"
                    >
                        <RewardsIcon className="brand-icons" />
                        <p className="menuFont">REWARDS</p>
                    </Nav.Link>
                </Nav.Item>
            )}
            <Nav.Item>
                <Nav.Link
                    disabled={navBarButtonDisable}
                    eventKey="5"
                    className="d-flex flex-row navBarBrandLink verticalLine justify-content-start text-nowrap"
                    as={Link}
                    to="/giftcards"
                >
                    <Image
                        className="brand-icons"
                        src={giftCardIcon}
                        alt="giftcard icon"
                    />
                    <p className="menuFont">GIFT CARDS</p>
                </Nav.Link>
            </Nav.Item>
            <Nav.Item>
                {(!user || isGuest) && (
                    <Nav.Link
                        eventKey="6"
                        className="d-flex flex-row navBarBrandLink verticalLine"
                        onClick={handleModal}
                    >
                        <FaUserCircle size={22} className="primary-opt" />
                        <p className="menuFont">SIGN IN</p>
                    </Nav.Link>
                )}
                {user && !isGuest && (
                    <Nav.Link
                        eventKey="7"
                        className="d-flex flex-row ms-1 navBarBrandLink verticalLine"
                        onClick={handleLogout}
                    >
                        <FaUserCircle size={22} className="primary-opt" />
                        <p className="menuFont">LOGOUT</p>
                    </Nav.Link>
                )}
            </Nav.Item>
        </>
    );

    const navSideItems = () => (
        <div className="text-start">
            <Nav.Item className="navItem">
                <Nav.Link
                    disabled={navBarButtonDisable}
                    eventKey="1"
                    className="d-flex flex-row justify-content-start align-items-center nav-menu-link"
                    onClick={() => {
                        redirectToMenu();
                    }}
                >
                    <MenuIcon className="brand-icons" />
                    <p className="sideMenuFont">MENU</p>
                </Nav.Link>
            </Nav.Item>
            <Nav.Item className="navItem">
                <Nav.Link
                    disabled={navBarButtonDisable}
                    eventKey="2"
                    className="d-flex flex-row justify-content-start align-items-center nav-menu-link"
                    as={Link}
                    to="/"
                >
                    <LocationsIcon className="brand-icons" />
                    <p className="sideMenuFont">LOCATION</p>
                </Nav.Link>
            </Nav.Item>
            <Nav.Item className="navItem">
                <Nav.Link
                    disabled={navBarButtonDisable}
                    eventKey="3"
                    className="d-flex flex-row justify-content-start align-items-center nav-menu-link"
                    href={configs?.brandConfigs?.NUTRITION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <NutritionIcon className="brand-icons" />
                    <p className="sideMenuFont">NUTRITION</p>
                </Nav.Link>
            </Nav.Item>
            {user && !isGuest && (
                <Nav.Item className="navItem">
                    <Nav.Link
                        disabled={navBarButtonDisable}
                        eventKey="4"
                        className="d-flex flex-row justify-content-start align-items-center nav-menu-link"
                        as={Link}
                        to="/recentorders"
                    >
                        <RecentOrdersIcon className="brand-icons" />
                        <p className="sideMenuFont" style={{ whiteSpace: "nowrap" }}>ORDERS</p>
                    </Nav.Link>
                </Nav.Item>
            )}
            {user && !isGuest && (
                <Nav.Item className="navItem">
                    <Nav.Link
                        disabled={navBarButtonDisable}
                        eventKey="5"
                        className="d-flex flex-row justify-content-start align-items-center nav-menu-link"
                        as={Link}
                        to="/rewards"
                    >
                        <RewardsIcon className="brand-icons" />
                        <p className="sideMenuFont">REWARDS</p>
                    </Nav.Link>
                </Nav.Item>
            )}
            <Nav.Item className="navItem">
                <Nav.Link
                    disabled={navBarButtonDisable}
                    eventKey="5"
                    className="d-flex flex-row"
                    as={Link}
                    to="/giftcards"
                >
                    <Image
                        className="brand-icons"
                        src={giftCardIcon}
                        alt="giftcard icon"
                    />
                    &nbsp;&nbsp;
                    <p className="sideMenuFont">GIFT CARDS</p>
                </Nav.Link>
            </Nav.Item>
            <Nav.Item className="navItem">
                {(!user || isGuest) && (
                    <Nav.Link
                        eventKey="6"
                        className="d-flex flex-row justify-content-start align-items-center nav-menu-link"
                        onClick={handleModal}
                    >
                        <FaUserCircle size={20} className="primary-opt mx-1" />
                        <p className="sideMenuFont signInMenu">SIGN IN</p>
                    </Nav.Link>
                )}
                {user && !isGuest && (
                    <Nav.Link
                        eventKey="7"
                        className="d-flex flex-row ms-1 justify-content-start align-items-center nav-menu-link"
                        onClick={handleLogout}
                    >
                        <FaUserCircle size={20} className="primary-opt" />
                        <p className="sideMenuFont signInMenu">LOGOUT</p>
                    </Nav.Link>
                )}
            </Nav.Item>
        </div>
    );

    const showSideMenu = () => (
        <Offcanvas
            className="off-canvas-menu"
            show={navbarExpanded}
            onHide={() => setNavbarExpanded(false)}
        >
            <Container className="d-flex justify-content-center pt-2 pb-1">
                <Navbar.Brand>
                    <Image
                        className="navBarBrandLogoDesk"
                        src={brandLogoPath}
                        alt={`${brandTitle} logo`}
                        onClick={() => redirectToBrandSite()}
                    />
                </Navbar.Brand>
            </Container>
            <Offcanvas.Header className="justify-content-end pb-0 pt-0" closeButton />
            <Offcanvas.Body>
                <Container className="pl-0 ml-0 moveLeft">
                    <UserProfileView displayMode="column" navbarExpanded onHide={() => setNavbarExpanded(false)} />
                </Container>
                <div className="userInfoGoldStroke" />
                {navSideItems()}
            </Offcanvas.Body>
        </Offcanvas>
    );

    function updateOrderErrorStatus(errorCode:string) {
        updateOrderErrorCode(errorCode);
        dispatch(toggleCartDrawer(true));
    }

    const handleOrderCreation = () => {
        if (!isSelectedRewardValid) {
            dispatch(setHasRewaredSelected(false));
            dispatch(updateSelectedReward({} as RewardsDetails));
            dispatch(updateRewardSelectedOnPage(false));
        }
        const chosenReward = (availableReward?.walletCode && hasRewardSelectedFromPage)
                ? {
                    walletCode: String(availableReward?.walletCode),
                    validateReward: orderId ? true : undefined,
                } : undefined;
        if (orderId) {
            updateOrderMutation({
                ...orderRequestPayload,
                orderId,
                chosenReward,
                }).then((response) => {
                const updateOrderResponse = response as unknown as OrderPlacementResponse;
                if (updateOrderResponse.error) {
                    updateOrderErrorStatus(updateOrderResponse.error.data.message.errors.title);
                } else {
                    updatePostOrderState(updateOrderResponse.data);
                }
            }).catch(() => {
                updateOrderErrorStatus('default');
            });
        } else {
            createOrderMutation({
                ...orderRequestPayload,
                chosenReward,
            }).then((response) => {
                const createOrderResponse = response as unknown as OrderPlacementResponse;
                if (createOrderResponse.error) {
                    updateOrderErrorStatus(createOrderResponse.error.data.message.errors.title);
                } else {
                    updatePostOrderState(createOrderResponse.data);
                }
            }).catch(() => {
                updateOrderErrorStatus('default');
            });
        }
    };
    const onTCartCheckout = () => {
        // Keep the Shell cart mounted while checkout is loading so it remains
        // the active owner of native Back and Pop to Home bridge events.
        if (!isShellApp) {
            dispatch(toggleCartDrawer(false));
        }
        if (!user && isGuest && isShellApp) {
            // Shell app should not prompt user for guest confirmation.
            const guestBody: GuestRequest = {
                deviceId: uniqueId,
            };
            createGuestToken(guestBody).then((response) => {
                const guestTokenId = response as unknown as {data:GuestResponseType};
                const decryptedRespoonse: string = decrypt(guestTokenId.data.value);
                const {aud, token} = JSON.parse(decryptedRespoonse) as GuestResponse;
                deleteKeyPersist('user');
                deleteKeyPersist('token');
                deleteKeyPersist('isGuest');
                dispatch(setCredentials({user: aud, token, isGuest: true}));
                handleOrderCreation();
            }).catch(() => undefined);
        } else if (user) {
            handleOrderCreation();
        } else {
            if (isShellApp) {
                dispatch(toggleCartDrawer(false));
            }
            dispatch(toggleAuthModal(true));
        }
    };
    const onPickupInfoChange = () => {
        dispatch(toggleCartDrawer(false));
        dispatch(updateShowChangePickUpDetailsModal(true));
    };
    const onTErrorClose = () => {
        updateOrderErrorCode(null);
    };
    const onGuestUserReady = () => {
        dispatch(toggleAuthModal(false));
        handleOrderCreation();
    };
    return (
        <>
            <ModalLoader showModal={isAnyApiLoading} />
            <WelcomeModal />
            <EnrollModal />
            <AutoHideSnackbar />
            {isFutureOrdersEnabled
            ? (
                storeNumber && (
                    <PickupModal
                        pickUpTime={pickUpTime}
                        pickUpDate={pickUpDate}
                        pickUpType={pickUpType}
                        storeId={storeNumber}
                        open={showChangePickUpDetailsModal}
                        onClose={hidePickupModal}
                        onChange={handlePickupInfoChange}
                    />
                )
            )
            : (
                <Modal
                show={showChangePickUpDetailsModal}
                onHide={hidePickupModal}
                centered
                backdrop="static"
                >
                    <PickUpSelectionsFromAnywhere />
                </Modal>
            )}
            <BrowserView>
                <Navbar
                    collapseOnSelect
                    className="navBarBackground browserViewNavBar"
                    expand="xxl"
                    variant="light"
                    //fixed="top"
                    expanded={navbarExpanded}
                    onToggle={() => setNavbarExpanded(!navbarExpanded)}
                >
                    <Container className="d-flex flex-row" fluid>
                        <div className="d-flex align-items-center">
                            <div className="navbar-toggle">
                                {!hideNavigation
                                    && (
                                        <Navbar.Toggle className="d-flex" aria-controls="responsive-navbar-nav">
                                            <BsJustify />
                                        </Navbar.Toggle>
                                    )}
                            </div>
                            <div
                                className="navbar-buttons-showing navbar-buttons-hidden d-flex"
                            >
                                <Navbar.Brand>
                                    <Image
                                        className="navBarBrandLogoDesk"
                                        src={brandLogoPath}
                                        alt={`${brandTitle} logo`}
                                        onClick={() => redirectToBrandSite()}
                                    />
                                </Navbar.Brand>
                            </div>
                            <Nav>
                                <Navbar.Collapse
                                    id="responsive-navbar-nav"
                                >
                                    {!hideNavigation && showSideMenu()}
                                    <Container className="pt-2 navbar-show-hide">
                                        {!navbarExpanded && !hideNavigation && navItems()}
                                    </Container>
                                    <LoginView
                                        isOpen={modalOpen}
                                        hideModal={hideModal}
                                        isTStore={isTStore}
                                        onGuestUserReady={onGuestUserReady}
                                    />
                                    {!hideNavigation
                                        && (
                                            <CartDrawer
                                                anchor="right"
                                                open={cartOpen}
                                                onClose={closeCart}
                                            >
                                                {
                                                    isTStore
                                                    ? (
                                                        <TCart
                                                            onPickupInfoChange={onPickupInfoChange}
                                                            onCartCheckout={onTCartCheckout}
                                                            errorCode={orderErrorCode}
                                                            onErrorClose={onTErrorClose}
                                                            isAnyApiLoading={isAnyApiLoading}
                                                        />
                                                    )
                                                    : <Cart cartItems={itemsInCart} />
                                                }
                                            </CartDrawer>
                                        )}
                                </Navbar.Collapse>
                            </Nav>
                        </div>
                        <div className="d-flex flex-row ml-auto justify-content-end align-items-center">
                            {!navbarExpanded && !hideNavigation
                                && (
                                    <Container className="navbar-show-hide pt-1">
                                        <UserProfileView />
                                    </Container>
                                )}
                            {!hideNavigation && !isShellApp && (
                                <IconButton
                                    className="d-flex flex-row justify-content-end align-items-center iconStyles cart-badge-count"
                                    disabled={navBarButtonDisable}
                                    onClick={openCart}
                                >
                                    <Badge badgeContent={totalItemsInTheCart}>
                                        <AddShoppingCart
                                            aria-hidden="false"
                                            aria-label={totalItemsInTheCart === 0
                                            ? ARIA_LABELS.CART.emptyCart
                                            : ARIA_LABELS.CART.cartWithItems(totalItemsInTheCart)}
                                        />
                                    </Badge>
                                </IconButton>
                            )}
                        </div>
                    </Container>
                </Navbar>
            </BrowserView>
            <MobileView>
                { !isShellApp && (
                <div
                    className="d-flex align-items-center justify-content-between navBarBackgroundMobile mobileNavBar vw-100"
                >
                    <div className="d-flex align-items-center">
                        <Navbar.Brand className="navBarBrand">
                            <Image
                                className="m-1 navBarBrandLogo"
                                src={brandLogoPath}
                                alt="logo"
                                style={{ paddingLeft: "0px" }}
                                onClick={() => redirectToBrandSite()}
                            />
                        </Navbar.Brand>
                    </div>
                    {(!hideNavigation) && (
                        <div className="d-flex align-items-center">
                            {user && <UserProfileView />}
                            {isGuest && (
                                <Button
                                    variant="link"
                                    className="signInFont text-nowrap text-decoration-none"
                                    onClick={() => {
                                        openMobileSignup();
                                    }}
                                >
                                    SIGN IN
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                )}
                <LoginView
                    isOpen={modalOpen}
                    hideModal={hideModal}
                    isTStore={isTStore}
                    onGuestUserReady={handleOrderCreation}
                />
                {!hideNavigation
                    && (
                        <CartDrawer
                            anchor="right"
                            open={cartOpen}
                            onClose={closeCart}
                        >
                            {
                                isTStore
                                ? (
                                    <TCart
                                        onPickupInfoChange={onPickupInfoChange}
                                        onCartCheckout={onTCartCheckout}
                                        errorCode={orderErrorCode}
                                        onErrorClose={onTErrorClose}
                                        isShellApp={isShellApp}
                                        isAnyApiLoading={isAnyApiLoading}
                                    />
                                ) : <Cart cartItems={itemsInCart} />
                            }
                        </CartDrawer>
                    )}
                <CartDrawer
                    anchor="left"
                    open={showMoreOptions}
                    onClose={closeMoreOptions}
                >
                    <MoreOptions />
                </CartDrawer>
            </MobileView>
            {/* Without this condition here, the UpSellModal is mounting and
            api calls are happening even though modal is not showing on the browser */}
            {upsellModalOpen
            && (
            <UpsellModal
                showUpsellModal={upsellModalOpen}
                onUpsellModalClose={closeUpsellModal}
                storeNumber={storeNumber}
                isToastStore={isTStore}
            />
            )}
            {isShellApp && shouldShowShellCart && (
                <ShellCartFloatingButton
                    disabled={navBarButtonDisable}
                    onClick={openCart}
                    totalItems={totalItemsInTheCart}
                />
            )}
        </>
    );
};

export default NavbarComp;
